/**
 * Report aggregation. Pure functions, no I/O — the app builds a report to show
 * on screen and the Worker renders the very same shape into the published page,
 * so what a client sees always matches what was previewed.
 */

import type {
  Client,
  DateRange,
  Entry,
  Report,
  ReportGrouping,
  ReportRow,
} from './types';
import { inRange, toMonthKey } from './dates';

/** Group entries into report rows. Rows are ordered oldest first, as clients read them. */
export function buildRows(entries: Entry[], grouping: ReportGrouping): ReportRow[] {
  const buckets = new Map<string, { hours: number; notes: string[]; entryCount: number }>();

  // Sort ascending first so merged notes come out in date order.
  const ordered = [...entries].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  for (const e of ordered) {
    const key = grouping === 'month' ? toMonthKey(e.date) : e.date;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { hours: 0, notes: [], entryCount: 0 };
      buckets.set(key, bucket);
    }
    bucket.hours += e.hours;
    bucket.entryCount += 1;

    const note = e.notes?.trim();
    // Dedupe: the same short note recurs constantly ("misc", "sync meeting")
    // and repeating it inside one bucket adds nothing for the reader.
    if (note && !bucket.notes.includes(note)) bucket.notes.push(note);
  }

  return [...buckets.entries()]
    .map(([key, b]) => ({ key, hours: round2(b.hours), notes: b.notes, entryCount: b.entryCount }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

/**
 * Build a complete report for one client over a date range.
 *
 * `allEntries` may span any period; it is filtered here so callers can hand over
 * whatever they have cached without pre-trimming.
 */
export function buildReport(
  client: Client,
  allEntries: Entry[],
  range: DateRange,
  grouping: ReportGrouping,
): Report {
  const entries = allEntries.filter(
    (e) => e.clientId === client.id && inRange(e.date, range.from, range.to),
  );
  const rows = buildRows(entries, grouping);
  const hours = round2(rows.reduce((sum, r) => sum + r.hours, 0));

  const report: Report = {
    clientName: client.name,
    range,
    grouping,
    rows,
    totals: {
      hours,
      amount: client.rate !== undefined ? round2(hours * client.rate) : undefined,
      currency: client.currency,
    },
    generatedAt: new Date().toISOString(),
  };

  if (client.retainer) {
    report.retainer = {
      size: client.retainer,
      used: hours,
      balance: round2(client.retainer - hours),
    };
  }

  return report;
}

/** Hours and money both want 2dp; float sums drift without this. */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Monthly totals across a whole client history, for the dashboard. */
export function monthlyTotals(entries: Entry[]): { month: string; hours: number }[] {
  return buildRows(entries, 'month').map((r) => ({ month: r.key, hours: r.hours }));
}

/** RFC 4180 CSV. Quotes every field so notes with commas or newlines survive. */
export function toCsv(report: Report): string {
  const header = report.grouping === 'month' ? 'Month' : 'Date';
  const lines = [
    [header, 'Hours', 'Notes'].map(csvCell).join(','),
    ...report.rows.map((r) => [r.key, String(r.hours), r.notes.join('; ')].map(csvCell).join(',')),
    ['Total', String(report.totals.hours), ''].map(csvCell).join(','),
  ];
  return lines.join('\r\n');
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}
